import nsstr from 'nsstr'

import {
  imOpToObject
} from '@bumblehead/im'

import {
  gnnodeChildKeysGet as nodeChildKeysGet
} from '../gnnode.js'

import {
  gnpublish_graphsubj
} from '../gnpublish.js'

import {
  gnerrnode_keynodenotfound,
  gnerrNodeNotFound as errNodeNotFound,
  gnerrnode_nspropresolvedundefined,
  gnerrClientNSForeignPathNotFound as errClientNSForeignPathNotFound
} from '../gnerr.js'

import {
  gnaccNSMap as accNSMap,
  gnaccBaseElem as accBaseElem
} from '../gnacc.js'

import {
  gnenumNSSUBJ as NSSUBJ
} from '../gnenum.js'

import {
  gnutilNonNullObjIs as utilNonNullObjIs,
  gnutilPlainObjIs as utilPlainObjIs
} from '../gnutil.js'

// return the value defined on the given namespace or null
//
// ex,
//
//   o.objlookup('hello.my', {hello:{my:'world'}})
//
// return,
//
//   'world'
//
const objlookup = (nsstr, obj) => (
  String(nsstr).split('.').reduce(
    (a, b) => a ? (b in a ? a[b] : a[Number(b)]) : undefined, obj))

const keypathjoin = (keypath, relpath) => {
  return relpath.startsWith('/')
    ? relpath
    : new URL(relpath, new URL(`http://dummy.com${keypath}/`)).pathname
}

// nsstr.parse('[/dataerrors].subj.childremoved')
// { fullstr: '[/dataerrors].subj.childremoved',
//   nsstr: 'subj.childremoved',
//   nskey: 'subj',
//   nsprop: 'childremoved',
//   fnsstr: '/dataerrors',
//   fnspath: '/dataerrors',
//   fnskey: undefined,
//   fnsprop: undefined }
//
// nsstr.parse('[fkey.shapedata].subj.geometry');
// { fullstr: '[fkey.shapedata].subj.geometry',
//   nsstr: 'subj.geometry',
//   nskey: 'subj',
//   nsprop: 'geometry',
//   fnsstr: 'fkey.shapedata',
//   fnspath: undefined,
//   fnskey: 'fkey',
//   fnsprop: 'shapedata' }
//
// nsstr.parse('pkg.start')
// { fullstr: 'pkg.start',
//   nsstr: 'pkg.start',
//   nskey: 'pkg',
//   nsprop: 'start',
//   fnsstr: undefined,
//   fnspath: undefined,
//   fnskey: undefined,
//   fnsprop: undefined }
//
// ```
// {
//   'gn:type': 'client',
//   name: 'incompletetotal',
//   nsref: '[/dataitems].such.incompletetotal',
//   chain: ["gn:CHAIN", ".client.itemscacheget", [
//     ['nsref', ['[/dataitems].such.incompletetotal']]
//   ]]
// }
// ```
const $nsref = async (gst, qst, args) => {
  const graph = gst.graph
  const node = gst.node
  const nsref = args[0]
  const nsobj = nsstr(nsref)
  const fnspath = nsobj.fnspath

  if (fnspath) {
    const fnpath = fnspath === './'
      ? node.get('key')
      : keypathjoin(node.get('key'), fnspath)
    const fnnode = fnspath === './'
      ? node
      : graph.get(fnpath)

    if (!fnnode) {
      throw errClientNSForeignPathNotFound(graph, node, nsref, fnspath)
    }

    if (!nsobj.nskey) {
      qst.target = fnnode

      return qst
    }

    const fnnodens = accNSMap[nsobj.nskey].get(fnnode)
    const fnnodenspropval = nsobj.nsprop
      ? utilPlainObjIs(fnnodens) && objlookup(nsobj.nsprop, fnnodens)
      : fnnodens

    if (
      fnnodenspropval === null || fnnodenspropval === undefined
    ) {
      throw new Error(`property lookup "${nsobj.nsprop}" failed.`
        + `\nfrom "${nsobj.nskey}": ` + JSON.stringify(fnnodens)
        + `\nfrom fnode: ${fnnode.get('key')}`)
    }
    qst.target = fnnodenspropval

    return qst
  } else if (nsobj.fnsstr) {
    // fnsstr ex, 'fkey.shapedata'
    const nodens = accNSMap[nsobj.fnskey].get(node, {})
    const fnpath = nodens[nsobj.fnsprop]
    const fnnode = graph.get(fnpath)
    if (!fnnode) {
      throw new Error(`node not found: "${fnpath}"`)
    }

    if (!nsobj.nskey) {
      // only the fnnode is referenced, eg, '[fkey.sequence]'
      qst.target = fnnode

      return qst
    }

    const fnnodens = accNSMap[nsobj.nskey].get(fnnode)//node.get()
    if (!fnnodens) {
      throw new Error(`node ns not found: "${nsobj.fnskey}", `
        + `\`${fnnode.get('key')}\``)
    }
    const fnnodenspropval = utilNonNullObjIs(fnnodens)
      && objlookup(nsobj.nsprop, fnnodens)

    if (
      fnnodenspropval === null || fnnodenspropval === undefined
    ) {
      throw new Error(`node ns not found: "${nsobj.nsprop}", `
        + JSON.stringify(imOpToObject(fnnode)))
    }

    qst.target = fnnodenspropval

    return qst
  } else {
    // requires newer nsstr package
    //const nodens=accNSMap[nsobj.nskey || nsobj.nsstr].get(node)
    const nodens = accNSMap[nsobj.nskey].get(node)
    const refval = nsobj.nsprop
      ? nodens[nsobj.nsprop]
      : nodens

    //  [ 'nsref', [ 'subj.iscompleted', [Object] ] ],
    //  [ 'childkeys', [] ],
    //  [ 'fold', [ 0, [Array] ] ]
    if (refval === null || refval === undefined) {
      throw new Error(`node ns not found: "${nsobj.nsprop || nodens}", `
        + JSON.stringify(imOpToObject(node)))
    }

    qst.target = refval

    return qst
    // {fullstr: 'pkg.start',nsstr: 'pkg.start',nskey: 'pkg',nsprop: 'start'}
    // value is 'local', not foreign
  }
}

const $childfold = async (gst, qst, args, run) => {
  const foldargs = args.slice(0, 3)
  const foldFn = args[3]
  const res = await run(gst, qst, foldFn, foldargs)

  qst.target = res

  return qst
}

const $childkeys = async (gst, qst, args) => {
  const parentkey = args[0]
  const target = qst.target
  const graph = gst.graph
  const node = gst.node

  const nddata = parentkey
    ? graph.get(parentkey)
    : typeof target === 'string' ? graph.get(target) : (target || node)

  if (!nddata)
    throw gnerrnode_keynodenotfound(graph, node, parentkey)

  qst.target = nodeChildKeysGet(nddata).slice()

  return qst
}

const $ns = (gst, qst, args) => {
  const gr = gst.graph

  const nsstr = args[0]
  const node = typeof qst.target === 'string'
    ? gr.get(qst.target)
    : qst.target || gst.node
  const nskey = nsstr.split('.')[0]
  const ns = accNSMap[nskey].get(node, {})
  const res = nsstr === nskey
    ? ns
    : objlookup(nsstr.slice(nskey.length + 1), ns)

  if (typeof res === 'undefined') {
    throw gnerrnode_nspropresolvedundefined(gr, node, ns, nsstr)
  }

  qst.target = res

  return qst
}

// Resolves a node from the application graph
//
// ```
// q.node(nodekey|'this'|undefined) → node
// ```
const $node = async (gst, qst, args, run) => {
  const graph = gst.graph
  const node = gst.node
  const resnodekey = await run(gst, qst, args[0])
  const resnode = (!resnodekey || resnodekey === 'this')
    ? node
    : graph.get(resnodekey)

  if (typeof resnode === 'undefined')
    throw errNodeNotFound(graph, node, resnodekey)

  qst.target = resnode

  return qst
}

const $nodekey = (gst, qst, args) => {
  qst.target = (!args[0] || args[0] === 'this')
    && gst.node.get('key')

  return qst
}

const $baseelem = (gst, qst, args) => {
  const nsstr = args[0]
  const baseelem = (gst.nhy && 'baseelem' in gst.nhy)
    ? gst.nhy.baseelem
    : accBaseElem.get(gst.node)

  qst.target = nsstr
    ? objlookup(nsstr, baseelem)
    : baseelem

  return qst
}

const $part = (gst, qst, args) => {
  const nsstr = args[0]
  const nhy = gst.nhy
  // TODO determine part or no-part, always?
  // when building parts..
  // remove nhy from parts construction
  const part = nhy.part || nhy

  qst.target = nsstr
    ? objlookup(nsstr, part)
    : part

  return qst
}

const $nhylook = (gst, qst, args) => {
  const nsstr = args[0]

  qst.target = nsstr
    ? objlookup(nsstr, gst.nhy)
    : gst.nhy

  return qst
}

// array.applysubj([selector1, selector2...]) → array
// object.applysubj([selector1, selector2...]) → object
const $applysubj = async (gst, qst, args, run) => {
  const target = qst.target || gst.node
  const graph = gst.graph
  const sess = gst.sess
  const cfg = gst.cfg

  const subjdata = await run(gst, qst, args[0])
  const pxucreate = nd => (
    [typeof nd === 'string' ? nd : nd.get('key'), NSSUBJ, subjdata])
  const pxuarr = Array.isArray(target)
    ? target.map(pxucreate)
    : [pxucreate(target)]

  const res = await gnpublish_graphsubj(sess, cfg, graph, pxuarr)

  gst.graph = res[0]

  return qst
}

export {
  $ns,
  $node,
  $nodekey,
  $nsref,
  $nhylook,
  $childfold,
  $childkeys,
  $baseelem,
  $applysubj,
  $part
}
